// frontend/src/app/pdf-corpus/domain/pdf-corpus.service.ts
import { Injectable, inject, signal } from '@angular/core';
import { defer, MonoTypeOperatorFunction, Observable, tap } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { PDF_CORPUS_PORT } from '../ports/pdf-corpus.port';
import {
  BatchUploadEvent,
  ExtractionState,
  ExtractionStatusDto,
  PdfDocument,
  UploadOutcome,
} from './types';

@Injectable()
export class PdfCorpusService {
  private readonly port = inject(PDF_CORPUS_PORT);

  private readonly _uploading = signal(false);
  private readonly _uploadProgress = signal<number | null>(null);
  private readonly _lastError = signal<Error | null>(null);
  private readonly _extractionState = signal<Map<number, ExtractionState>>(new Map());

  readonly uploading = this._uploading.asReadonly();
  readonly uploadProgress = this._uploadProgress.asReadonly();
  readonly lastError = this._lastError.asReadonly();
  readonly extractionState = this._extractionState.asReadonly();

  /**
   * Upload one or more PDFs. Signals (`uploading`, `uploadProgress`) reset
   * synchronously when called (before subscription). Callers MUST subscribe.
   * Per-file failures are surfaced in the returned `UploadOutcome[]` — they
   * do NOT set `lastError`. `lastError` is only set if the entire request
   * fails (network error, 5xx).
   */
  uploadAll(groupId: number, files: File[]): Observable<UploadOutcome[]> {
    this._uploading.set(true);
    this._uploadProgress.set(0);
    this._lastError.set(null);

    return defer(() => this.port.uploadAll(groupId, files)).pipe(
      tap((ev) => {
        if (ev.kind === 'progress') this._uploadProgress.set(ev.percent);
      }),
      filter((ev): ev is Extract<BatchUploadEvent, { kind: 'done' }> => ev.kind === 'done'),
      map((ev) => ev.results),
      this.trackErrorAndCleanup(),
    );
  }

  listForGroup(groupId: number): Observable<PdfDocument[]> {
    return this.port.listForGroup(groupId).pipe(this.trackError());
  }

  fetchOne(groupId: number, pdfId: number): Observable<PdfDocument> {
    return this.port.fetchOne(groupId, pdfId).pipe(this.trackError());
  }

  fetchBytes(groupId: number, pdfId: number): Observable<Blob> {
    return this.port.fetchBytes(groupId, pdfId).pipe(this.trackError());
  }

  refreshExtractionState(groupId: number, pdfId: number): Observable<ExtractionState> {
    return this.port.fetchExtractionState(groupId, pdfId).pipe(
      tap((state) => {
        const next = new Map(this._extractionState());
        next.set(pdfId, state);
        this._extractionState.set(next);
      }),
      this.trackError(),
    );
  }

  triggerExtraction(groupId: number, pdfId: number): Observable<ExtractionStatusDto> {
    return this.port.triggerExtraction(groupId, pdfId).pipe(
      tap((job) => {
        const next = new Map(this._extractionState());
        next.set(pdfId, { kind: 'JOB', job });
        this._extractionState.set(next);
      }),
      this.trackError(),
    );
  }

  materializePdf(groupId: number, paperId: number): Observable<number> {
    return this.port.materializePdf(groupId, paperId).pipe(this.trackError());
  }

  extractionFor(pdfId: number): ExtractionState | undefined {
    return this._extractionState().get(pdfId);
  }

  clearError(): void {
    this._lastError.set(null);
  }

  private toError(err: unknown): Error {
    return err instanceof Error ? err : new Error(String(err));
  }

  private trackError<T>(): MonoTypeOperatorFunction<T> {
    return tap<T>({ error: (err) => this._lastError.set(this.toError(err)) });
  }

  private trackErrorAndCleanup<T>(): MonoTypeOperatorFunction<T> {
    return tap<T>({
      next: () => {
        this._uploading.set(false);
        this._uploadProgress.set(null);
      },
      error: (err) => {
        this._lastError.set(this.toError(err));
        this._uploading.set(false);
        this._uploadProgress.set(null);
      },
    });
  }
}
